'use client';

import React, { useState } from 'react';
import type { Niche } from '@/lib/rateCalculator';
import { ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface NicheSelectorProps {
  selected: Niche;
  onChange: (niche: Niche) => void;
}

const nichePremiumTier: Record<Niche, 'high' | 'medium' | 'standard'> = {
  finance: 'high',
  tech: 'high',
  business: 'high',
  health: 'high',
  beauty: 'medium',
  fitness: 'medium',
  travel: 'medium',
  fashion: 'medium',
  education: 'medium',
  food: 'standard',
  sports: 'standard',
  gaming: 'standard',
  lifestyle: 'standard',
  parenting: 'standard',
  music: 'standard',
};

const tierColors: Record<'high' | 'medium' | 'standard', string> = {
  high: 'text-positive',
  medium: 'text-accent',
  standard: 'text-muted-foreground',
};

const tierBg: Record<'high' | 'medium' | 'standard', string> = {
  high: 'bg-positive/10 border-positive/30',
  medium: 'bg-accent/10 border-accent/30',
  standard: 'bg-muted/40 border-border',
};

export default function NicheSelector({ selected, onChange }: NicheSelectorProps) {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const niches = Object.keys(t.niches) as Niche[];

  return (
    <div className="relative">
      <label className="block text-sm font-semibold text-foreground mb-3 tracking-wide">
        {t.form.niche}
      </label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 input-field text-sm font-medium"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div className="flex items-center gap-2">
          <span className="text-foreground">{t.niches[selected]}</span>
          <span className={`niche-badge border ${tierBg[nichePremiumTier[selected]]} ${tierColors[nichePremiumTier[selected]]}`}>
            <TrendingUp size={9} />
            {t.nichePremiums[selected]}
          </span>
        </div>
        {open ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
      </button>

      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-2 z-30 rounded-xl border border-border overflow-hidden shadow-2xl"
          style={{ background: '#130D2E' }}
          role="listbox"
        >
          <div className="p-2 max-h-56 overflow-y-auto scrollbar-hide">
            {niches.map((niche) => {
              const tier = nichePremiumTier[niche];
              const isSelected = niche === selected;
              return (
                <button
                  key={`niche-${niche}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(niche);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all duration-100 ${
                    isSelected
                      ? 'bg-primary/20 text-primary' :'text-foreground hover:bg-muted/40'
                  }`}
                >
                  <span className="font-medium">{t.niches[niche]}</span>
                  <span className={`text-xs font-semibold ${tierColors[tier]}`}>
                    {t.nichePremiums[niche]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}